import type { FlightLeg } from '../../../domain/types'
import type { FlightDirection } from './FlightDirectionCard'
import * as Accordion from '@sunwebgroup/uds-ui/accordion'
import { SegmentedControls } from '@sunwebgroup/uds-ui/segmented-controls'
import { useMemo, useState } from 'react'
import { useIntl } from '@/intl'
import { useBookingDetailsStore } from '../../../infrastructure/useBookingDetailsStore'
import { isPastBooking } from '../../selectors'
import { BookingDetailsSection } from '../BookingDetailsSection'

import { IconRow } from '../IconRow'
import styles from './FlightDetailsSection.module.css'
import { FlightGroupCards } from './FlightGroupCards'
import { formatParticipantNames, groupFlightsByParticipants } from './flightGroups'

function getDefaultFlightDirection(
  outbound: FlightLeg[] | null | undefined,
): FlightDirection {
  return outbound?.length ? 'outbound' : 'inbound'
}

export function FlightDetailsSection() {
  const { formatMessage } = useIntl()
  const flight = useBookingDetailsStore(state => state.flightInformation)
  const participants = useBookingDetailsStore(state => state.participants)
  const isBookingPast = useBookingDetailsStore(isPastBooking)
  const isCancelled = useBookingDetailsStore(state => state.isCancelled)
  const groups = useMemo(() => flight ? groupFlightsByParticipants(flight) : [], [flight])
  const defaultDirection = getDefaultFlightDirection(groups[0]?.outbound)
  const [activeDirection, setActiveDirection] = useState<FlightDirection>(() =>
    defaultDirection,
  )

  if (!groups.length) {
    return null
  }

  const showAccordions = groups.length > 1

  return (
    <BookingDetailsSection
      iconName="AirplaneTakingOff"
      title={formatMessage({ id: 'booking_details_flight_title' })}
      description={formatMessage({ id: 'booking_details_flight_description' })}
    >
      <SegmentedControls
        className={styles.controls}
        defaultActive={defaultDirection}
        fixed
        onActiveChange={({ value }) => setActiveDirection(value as FlightDirection)}
      >
        <SegmentedControls.Segment value="outbound">
          {formatMessage({ id: 'booking_details_flight_outbound' })}
        </SegmentedControls.Segment>
        <SegmentedControls.Segment value="inbound">
          {formatMessage({ id: 'booking_details_flight_inbound' })}
        </SegmentedControls.Segment>
      </SegmentedControls>
      {showAccordions
        ? (
            <Accordion.Root className={styles.accordion}>
              {groups.map((group, index) => (
                <Accordion.Item
                  key={group.key}
                  title={formatParticipantNames(group.participantIds, participants)}
                  defaultOpen={index === 0}
                >
                  <FlightGroupCards group={group} activeDirection={activeDirection} isPastBooking={isBookingPast} isCancelled={isCancelled} />
                </Accordion.Item>
              ))}
            </Accordion.Root>
          )
        : (
            <FlightGroupCards group={groups[0]} activeDirection={activeDirection} isPastBooking={isBookingPast} isCancelled={isCancelled} />
          )}
      <div className={styles.remark}>
        <IconRow
          glyph="InformationInCircle"
          description={formatMessage({ id: 'booking_details_flight_check_airline_notice' })}
        />
      </div>
    </BookingDetailsSection>
  )
}
